import { useRef } from 'react';
import { DarwinButton } from 'components/darwin-ui';
import { useTranslation } from 'react-i18next';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import styles from './index.module.css';
import {
  parseGeoJSONToSchema,
  parseKmlToSchema,
} from 'utils/geolocation/geoParsers';
import { PolygonItemUI, GeoParseError, GeoParseErrorType } from 'types/maps';
import { DEFAULT_POLY_OPTIONS } from 'utils/maps';
import { v4 as uuidv4 } from 'uuid';
import { FileExtensions } from 'constants/geolocation';

interface ImportButtonProps {
  onImport: (polygons: PolygonItemUI[]) => void;
  existingCount: number;
}

export const ImportButton = ({
  onImport,
  existingCount,
}: ImportButtonProps) => {
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    const name = file.name.toLowerCase();

    if (
      !name.endsWith(FileExtensions.GEOJSON) &&
      !name.endsWith(FileExtensions.KML)
    ) {
      openNotification(
        t('geolocation.modal.import.unsupported-format'),
        t('geolocation.modal.import.unsupported-format-description'),
        'error'
      );
      return;
    }

    const text = await file.text();
    let parsedPolygons: PolygonItemUI[] = [];

    try {
      if (name.endsWith(FileExtensions.GEOJSON)) {
        const parsed = JSON.parse(text);
        const result = parseGeoJSONToSchema(parsed);
        // Transform PolygonSchema to PolygonItemUI
        parsedPolygons = result.polygons.map((paths, index) => ({
          id: uuidv4(),
          paths,
          options: DEFAULT_POLY_OPTIONS,
        }));
      } else if (name.endsWith(FileExtensions.KML)) {
        const result = parseKmlToSchema(text);
        // Transform PolygonSchema to PolygonItemUI
        parsedPolygons = result.polygons.map((paths, index) => ({
          id: uuidv4(),
          paths,
          options: DEFAULT_POLY_OPTIONS,
        }));
      }
    } catch (err: unknown) {
      let titleKey = 'geolocation.modal.import.parse-error';
      let descKey = 'geolocation.modal.import.parse-error-description';
      let descParams: Record<string, string> = {
        message: t('geolocation.modal.import.file-corrupt'),
      };

      if (err instanceof GeoParseError) {
        switch (err.type) {
          case GeoParseErrorType.EMPTY_GEOJSON:
            titleKey = 'geolocation.modal.import.empty-geojson';
            descKey = 'geolocation.modal.import.empty-geojson-description';
            descParams = {};
            break;
          case GeoParseErrorType.INVALID_TYPE:
            titleKey = 'geolocation.modal.import.invalid-type';
            descKey = 'geolocation.modal.import.invalid-type-description';
            descParams = {};
            break;
          case GeoParseErrorType.INVALID_XML:
            titleKey = 'geolocation.modal.import.invalid-kml';
            descKey = 'geolocation.modal.import.invalid-kml-description';
            descParams = {};
            break;
        }
      }

      const title: string = t(titleKey);
      const desc: string = t(descKey, descParams);
      openNotification(title, desc, 'error');
      return;
    }

    if (!parsedPolygons || parsedPolygons.length === 0) {
      openNotification(
        t('geolocation.modal.import.no-polygons'),
        t('geolocation.modal.import.no-polygons-description'),
        'error'
      );
      return;
    }

    // Warn if replacing existing polygons
    if (existingCount > 0) {
      openNotification(
        t('geolocation.modal.import.replace-warning'),
        t('geolocation.modal.import.replace-warning-description', {
          count: existingCount,
        }),
        'warning'
      );
    }

    onImport(parsedPolygons);

    openNotification(
      t('geolocation.modal.import.preview-loaded', {
        count: parsedPolygons.length,
        plural: parsedPolygons.length > 1 ? 's' : '',
      }),
      t('geolocation.modal.import.preview-loaded-description'),
      'success'
    );
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={`${FileExtensions.GEOJSON},${FileExtensions.KML}`}
        style={{ display: 'none' }}
        onChange={e => {
          const f = e.target.files?.[0] || null;
          handleFile(f);
          if (inputRef.current) inputRef.current.value = '';
        }}
      />
      <DarwinButton
        onClick={() => inputRef.current?.click()}
        className={styles.importButton}
      >
        {t('geolocation.modal.import.button')}
      </DarwinButton>
    </>
  );
};
